import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FileText, AlertCircle, Loader, ChevronDown, Search, X } from 'lucide-react';
import Footer from './Footer';

const ServiceCard = ({ id, title, price }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const handleBuyNow = () => {
    navigate(`/services/${id}/user-data`, { state: { service: { id, title, price } } });
  };

  const cardClasses = `rounded-lg p-6 text-center transition-all duration-300 hover:shadow-lg cursor-pointer ${isHovered
    ? 'bg-gradient-to-br from-[#1D3A50] to-[#182F40] text-white'
    : 'bg-white border border-gray-200 hover:border-gray-300'
    }`;

  const iconBgClasses = `w-16 h-16 mx-auto rounded-lg flex items-center justify-center mb-4 ${isHovered ? 'bg-white bg-opacity-20' : 'bg-gradient-to-br from-[#F2C231] to-[#C69A32]'
    }`;

  const textClasses = `${isHovered ? 'text-white' : 'text-gray-800'}`;

  const buttonClasses = `w-full py-1 px-2 md:py-2 md:px-4 rounded-lg font-semibold text-sm transition-all duration-200 bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white hover:from-[#C69A32] hover:to-[#F2C231]`;

  return (
    <div
      className={cardClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="mb-4">
        <div className={iconBgClasses}>
          <FileText className={`w-8 h-8 ${isHovered ? 'text-white' : 'text-white'}`} />
        </div>
      </div>
      <div className="h-16 mb-4 flex items-center justify-center">
        <h3 className={`text-sm font-semibold leading-tight text-center ${textClasses}`}>
          {title}
        </h3>
      </div>
      <div className="mb-4">
        <span className={`text-lg md:text-2xl font-bold ${textClasses}`}>€{price}</span>
      </div>
      <button onClick={handleBuyNow} className={buttonClasses}>
        ACQUISTA ORA
      </button>
    </div>
  );
};

const ServiceFeatures = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.selectedCategory || 'CAF');
  const [allServicesData, setAllServicesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(true);
  const [filteredServices, setFilteredServices] = useState([]);



  // Update the useEffect at the top to handle the incoming search query:
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setActiveTab(location.state.selectedCategory);
    }
    if (location.state?.searchQuery) {
      setSearchQuery(location.state.searchQuery);
      setIsSearchOpen(true);
    }
  }, [location.state]);

  // useEffect(() => {
  //   if (searchQuery.trim() === '') {
  //     setFilteredServices(servicesData);
  //   } else {
  //     const filtered = servicesData.filter(service =>
  //       service.title.toLowerCase().includes(searchQuery.toLowerCase())
  //     );
  //     setFilteredServices(filtered);
  //   }
  // }, [searchQuery, servicesData]);

  useEffect(() => {
    let filtered = allServicesData;

    // Filter by search query FIRST
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(service =>
        service.title.toLowerCase().includes(searchQuery.toLowerCase())
      );

      // AUTO-SWITCH TAB: If search has results, switch to the first matching service's category
      if (filtered.length > 0 && filtered[0].category !== activeTab) {
        setActiveTab(filtered[0].category);
      }
    } else {
      // No search query - just filter by active tab
      filtered = filtered.filter(service => service.category === activeTab);
    }

    setFilteredServices(filtered);
  }, [searchQuery, allServicesData]); // REMOVED activeTab from dependencies to avoid infinite loop

  // SEPARATE useEffect to handle tab changes when NOT searching
  useEffect(() => {
    if (searchQuery.trim() === '') {
      const filtered = allServicesData.filter(service => service.category === activeTab);
      setFilteredServices(filtered);
    }
  }, [activeTab, allServicesData, searchQuery]);

  const tabs = [
    { id: 'CAF', label: 'CAF' },
    { id: 'PATRONATO', label: 'PATRONATO' },
    { id: 'IMMIGRAZIONE', label: 'IMMIGRAZIONE' },
    { id: 'ALTRI SERVIZI', label: 'ALTRI SERVIZI' }
  ];

  useEffect(() => {
    const fetchAllServices = async () => {
      setIsLoading(true);
      setError('');
      try {
        // Fetch all categories at once
        const promises = tabs.map(tab =>
          axios.get(`https://api.cafpraticheonline.it/api/services?category=${tab.id}`)
        );

        const responses = await Promise.all(promises);

        // Combine all services with their category
        const allServices = responses.flatMap((response, index) =>
          response.data.map((service) => ({
            id: service.service_id,
            title: service.service_name,
            price: service.price,
            category: tabs[index].id // Add category to each service
          }))
        );

        setAllServicesData(allServices);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Failed to load services. Please ensure the backend is running.');
        setIsLoading(false);
      }
    };

    fetchAllServices();
  }, []); // Empty dependency - fetch only once

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsDropdownOpen(false);
  };

  const currentTab = tabs.find((tab) => tab.id === activeTab);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center mb-4 md:mb-8">
          <div className="flex items-center justify-center gap-4">
            <h1 className="text-xl md:text-6xl font-bold text-gray-800 mb-0 md:mb-1">SERVIZI</h1>

            {/* Search Icon/Bar - Absolute positioned to the right */}
            <div className="flex items-center">
              <div className="flex items-center gap-2 relative">

                {/* Search Icon (always visible) */}
                <button
                  onClick={() => {
                    if (isSearchOpen) {
                      setIsSearchOpen(false);
                      setSearchQuery("");
                    } else {
                      setIsSearchOpen(true);
                    }
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200 z-10"
                >
                  {/* {isSearchOpen ? (
                  <X className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                ) : (
                  <Search className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                )} */}
                </button>

                {/* Expanding Input */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isSearchOpen ? "w-48 md:w-64 opacity-100 ml-2" : "w-0 opacity-0"
                    }`}
                >
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Cerca servizi..."
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 w-full"
                    autoFocus={isSearchOpen}
                  />
                </div>



              </div>
            </div>
          </div>
          <div className="w-16 md:w-[40%] h-1 bg-yellow-400 mx-auto mb-4 md:mb-8 mt-2"></div>
        </div>

        {/* Desktop Tabs - Hidden on mobile */}
        <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${activeTab === tab.id
                ? 'bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white shadow-lg'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Mobile Dropdown - Visible only on mobile */}
        <div className="lg:hidden mb-6 relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full px-6 py-3 rounded-lg font-semibold text-sm bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white shadow-lg flex items-center justify-between"
          >
            <span>{currentTab?.label}</span>
            <ChevronDown
              className={`w-5 h-5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''
                }`}
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-10 overflow-hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full px-6 py-3 text-left font-semibold text-sm transition-colors ${activeTab === tab.id
                    ? 'bg-yellow-50 text-gray-800 border-l-4 border-[#F2C231]'
                    : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-20 flex flex-col items-center justify-center">
            <Loader className="animate-spin text-yellow-500 h-10 w-10 mb-4" />
            <p className="text-gray-600">Caricamento dei servizi...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20 flex flex-col items-center justify-center text-red-600">
            <AlertCircle className="h-10 w-10 mb-4" />
            <p>{error}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {filteredServices.length > 0 ? (
              filteredServices.map((service, index) => (
                <ServiceCard key={index} id={service.id} title={service.title} price={service.price} />
              ))
            ) : (
              <p className="text-center col-span-full text-gray-500">
                Nessun servizio trovato per questa categoria.
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ServiceFeatures;